// Oplevering en eindfactuur uit Bouwflow op de calculatie (blok C, SPEC_workflow 2): "opgeleverd ja of
// nee, en of er een door de klant ondertekende opleveringsbon is". Plus de open meldingen uit "Te doen".
import { Link } from 'react-router-dom';
import { OPLEVERINGSBON_LABEL, type WorkflowMelding, type WorkflowStaat } from '@recotex/shared';
import { fmtDate, fmtEUR } from '../lib/format';
import { Icon } from './AppShell';

export function WorkflowKenmerken({ w, verstuurd }: { w: WorkflowStaat; verstuurd: boolean }) {
  const o = w.oplevering;
  let oplevering: { cls: string; tekst: string; titel: string };
  if (w.opgeleverdVia === 'opleveringsbon' && o) {
    oplevering = { cls: 'chip-success', tekst: `Opgeleverd · ${fmtDate(o.getekendOp)}`, titel: `Opleveringsbon door de klant ondertekend op ${fmtDate(o.getekendOp)}` };
  } else if (w.opgeleverdVia === 'eindfactuur') {
    oplevering = { cls: 'chip-success', tekst: 'Opgeleverd · eindfactuur betaald', titel: 'Geen opleveringsbon, maar de eindfactuur is verstuurd en betaald: behandeld als opgeleverd (SPEC_workflow 3.2)' };
  } else if (o?.status === 'ok') {
    oplevering = { cls: 'chip-warning', tekst: 'Bon niet getekend door klant', titel: `Opleveringsbon van ${fmtDate(o.datum)}, nog niet ondertekend door de klant` };
  } else if (o?.status === 'provisional' || o?.status === 'denied') {
    oplevering = { cls: o.status === 'denied' ? 'chip-danger' : 'chip-warning', tekst: OPLEVERINGSBON_LABEL[o.status], titel: `${OPLEVERINGSBON_LABEL[o.status]} op ${fmtDate(o.datum)}${o.reden ? `: ${o.reden}` : ''}` };
  } else {
    oplevering = { cls: 'chip-neutral', tekst: 'Niet opgeleverd', titel: 'Nog geen opleveringsbon in Bouwflow' };
  }
  // 8.2.1: verstuurd naar de onderaannemer zonder eindfactuur naar de klant is een signaal.
  const eind =
    w.eindfactuur === 'betaald'
      ? { cls: 'chip-success', tekst: 'Eindfactuur betaald' }
      : w.eindfactuur === 'verstuurd'
        ? { cls: 'chip-neutral', tekst: 'Eindfactuur verstuurd' }
        : { cls: verstuurd ? 'chip-warning' : 'chip-neutral', tekst: 'Geen eindfactuur' };
  return (
    <>
      <span className={oplevering.cls} title={oplevering.titel}>{oplevering.tekst}</span>
      {w.opgeleverdVia !== 'eindfactuur' && (
        <span className={eind.cls} title={verstuurd && w.eindfactuur === 'geen' ? 'Verstuurd naar de onderaannemer, maar nog geen eindfactuur naar de klant (SPEC_workflow 8.2.1)' : 'Eindfactuur aan de klant in Bouwflow'}>
          {eind.tekst}
        </span>
      )}
    </>
  );
}

/** Open meldingen van de polling voor dit P-nummer, met de vervolgstap. */
export function WorkflowMeldingen({ meldingen, calcId }: { meldingen: WorkflowMelding[]; calcId: string }) {
  if (!meldingen.length) return null;
  return (
    <div className="mb-5 space-y-2">
      {meldingen.map((m) => {
        const wanneer = m.gebeurdOp ? ` op ${fmtDate(m.gebeurdOp)}` : '';
        const via = m.soort === 'opgeleverd' ? `Opleveringsbon ondertekend${wanneer}` : `Eindfactuur betaald${wanneer}, zonder opleveringsbon`;
        let tekst: React.ReactNode;
        if (m.situatie === 'vrijgave') {
          tekst = (
            <>
              <strong>Opgeleverd.</strong> {via}. Er staan posten open voor oplevering{m.details.openBedrag != null ? <> (samen <span className="tabular-nums">{fmtEUR(m.details.openBedrag)}</span>)</> : null}: voeg eventuele verrekeningen toe (schade, franchise, minwerken) en geef ze vrij.{' '}
              {m.calcId === calcId ? <a href="#open-posten" className="font-semibold underline">Naar de open posten</a> : m.calcId ? <Link to={`/project/${m.calcId}#open-posten`} className="font-semibold underline">Naar de calculatie met open posten</Link> : null}
            </>
          );
        } else if (m.situatie === 'calculatie_opgemaakt' && m.calcId === calcId) {
          tekst = (
            <>
              <strong>Automatisch opgemaakt</strong> uit de opstartfiche in Bouwflow na de oplevering ({via.toLowerCase()}). Kijk de posten na{' '}
              en zet de calculatie op definitief. Posten die de tool niet zeker kon koppelen, staan onderaan bij de overgeslagen regels.
            </>
          );
        } else {
          tekst = <><strong>Opgeleverd.</strong> {via}.</>;
        }
        return (
          <div key={m.id} className="notice flex items-start gap-2" role="status">
            <Icon name="info" size={18} className="mt-0.5 text-primary" />
            <p className="m-0 flex-1">{tekst}</p>
          </div>
        );
      })}
    </div>
  );
}
